import { useEffect, useRef } from "react";

interface AdUnitProps {
  /**
   * Ad slot ID from Google AdSense (e.g., "1234567890")
   * Replace with your actual ad slot ID after AdSense approval
   */
  adSlot: string;
  
  /**
   * Ad format type
   * - "auto": Responsive ads that adapt to screen size (recommended)
   * - "rectangle": 300x250 Medium Rectangle
   * - "leaderboard": 728x90 Leaderboard
   * - "mobile-banner": 320x100 Large Mobile Banner
   */
  adFormat?: "auto" | "rectangle" | "leaderboard" | "mobile-banner";
  
  /**
   * Ad style customization
   */
  className?: string;
  
  /**
   * Full width responsive (recommended for most placements)
   */
  fullWidthResponsive?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

/**
 * AdUnit Component
 * 
 * Reusable Google AdSense ad unit component for React SPAs.
 * Handles ad insertion, cleanup, and refresh on navigation.
 * 
 * Usage (after AdSense approval):
 * ```tsx
 * <AdUnit adSlot="your-ad-slot-id" adFormat="auto" fullWidthResponsive />
 * ```
 */
export const AdUnit = ({
  adSlot,
  adFormat = "auto",
  className = "",
  fullWidthResponsive = true,
}: AdUnitProps) => {
  const adRef = useRef<HTMLElement>(null);
  const isAdPushed = useRef(false);

  useEffect(() => {
    try {
      // Only push ad once per mount
      if (!isAdPushed.current && adRef.current) {
        // Initialize adsbygoogle array if not exists
        window.adsbygoogle = window.adsbygoogle || [];
        
        // Push ad to Google AdSense
        window.adsbygoogle.push({});
        isAdPushed.current = true;
        
        console.log(`[AdSense] Ad unit loaded: ${adSlot}`);
      }
    } catch (error) {
      console.error("[AdSense] Error loading ad:", error);
    }

    // Cleanup on unmount
    return () => {
      isAdPushed.current = false;
    };
  }, [adSlot]);

  // Get inline styles based on format
  const getAdStyle = () => {
    switch (adFormat) {
      case "rectangle":
        return { display: "inline-block", width: "300px", height: "250px" };
      case "leaderboard":
        return { display: "inline-block", width: "728px", height: "90px" };
      case "mobile-banner":
        return { display: "inline-block", width: "320px", height: "100px" };
      case "auto":
      default:
        return { display: "block" };
    }
  };

  return (
    <div 
      className={`ad-container ${className}`}
      style={{ 
        minHeight: adFormat === "auto" ? "100px" : undefined,
        textAlign: "center",
      }}
    >
      <ins
        ref={adRef as React.RefObject<HTMLModElement>}
        className="adsbygoogle"
        style={getAdStyle()}
        data-ad-client="ca-pub-4773206661966939"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      />
    </div>
  );
};

